/**
 * Material plugin that runs the shared participating-media model (shaders/mist.glsl)
 * inside every PBR material it is attached to.
 *
 * This is deliberately NOT a post-process: the mist is evaluated per fragment from the
 * fragment's own world position and the camera position, so the far end of the crane is
 * measurably deeper in haze than the near handrail, a floodlight housing 200m away dims
 * inside the air, and a future ship hull fades on exactly the ocean's curve. Babylon's
 * MaterialPluginBase is the engine-supported way to inject code into the PBR shader
 * itself — the haze participates in shading, it is not composited over the image.
 *
 * All instances read one shared parameter block (`mistState`), updated by the render
 * world whenever the environment changes, so every material breathes the same air.
 */

import { MaterialPluginBase } from '@babylonjs/core/Materials/materialPluginBase';
import type { Material } from '@babylonjs/core/Materials/material';
import type { UniformBuffer } from '@babylonjs/core/Materials/uniformBuffer';
import type { MaterialDefines } from '@babylonjs/core/Materials/materialDefines';
import type { Nullable } from '@babylonjs/core/types';

import mistGlsl from '../shaders/mist.glsl';

/** Shared air. One block for the whole scene; world.ts owns the values. */
export const mistState = {
  air: 0.0006,
  sea: 0.002,
  heightScale: 34,
  far: 0.0001,
  /** Dense, short-ranged air around the platform. See mistNearField in mist.glsl. */
  near: 0.0,
  nearRange: 150,
  colorR: 0.55,
  colorG: 0.58,
  colorB: 0.62,
};

export class MistPlugin extends MaterialPluginBase {
  /**
   * Whether this material takes the dense platform-level near field, 0 or 1.
   *
   * The two halves of the mist do different jobs and one surface can want only one of
   * them. The near field is thick air hanging around the rig inside 150 m; the air/sea/far
   * terms are aerial perspective, which is what makes a hull at two kilometres grey. The
   * creature's skin needs the second and is ruined by the first — full near field turned
   * its wet black chalk-white at close range, which is why it used to opt out of the mist
   * altogether and then stood at 2 km in undimmed black while every ship beside it faded.
   */
  private readonly nearFieldScale: number;

  constructor(material: Material, options?: { nearField?: boolean }) {
    // Priority 200: after the standard blocks so `finalColor` is fully lit when we fog it.
    super(material, 'DeepwaterMist', 200, { DEEPWATER_MIST: false });
    this.nearFieldScale = options?.nearField === false ? 0 : 1;
    this._enable(true);
  }

  override getClassName(): string {
    return 'MistPlugin';
  }

  override prepareDefines(defines: MaterialDefines): void {
    defines['DEEPWATER_MIST'] = true;
  }

  override getUniforms(): {
    ubo: { name: string; size: number; type: string }[];
    fragment: string;
  } {
    return {
      ubo: [
        { name: 'dwMistAir', size: 1, type: 'float' },
        { name: 'dwMistSea', size: 1, type: 'float' },
        { name: 'dwMistHeight', size: 1, type: 'float' },
        { name: 'dwMistFar', size: 1, type: 'float' },
        { name: 'dwMistNear', size: 1, type: 'float' },
        { name: 'dwMistNearRange', size: 1, type: 'float' },
        { name: 'dwMistColor', size: 3, type: 'vec3' },
      ],
      fragment: `#ifdef DEEPWATER_MIST
uniform float dwMistAir;
uniform float dwMistSea;
uniform float dwMistHeight;
uniform float dwMistFar;
uniform float dwMistNear;
uniform float dwMistNearRange;
uniform vec3 dwMistColor;
#endif`,
    };
  }

  override bindForSubMesh(uniformBuffer: UniformBuffer): void {
    uniformBuffer.updateFloat('dwMistAir', mistState.air);
    uniformBuffer.updateFloat('dwMistSea', mistState.sea);
    uniformBuffer.updateFloat('dwMistHeight', mistState.heightScale);
    uniformBuffer.updateFloat('dwMistFar', mistState.far);
    uniformBuffer.updateFloat('dwMistNear', mistState.near * this.nearFieldScale);
    uniformBuffer.updateFloat('dwMistNearRange', mistState.nearRange);
    uniformBuffer.updateFloat3('dwMistColor', mistState.colorR, mistState.colorG, mistState.colorB);
  }

  override getCustomCode(shaderType: string): Nullable<{ [pointName: string]: string }> {
    if (shaderType !== 'fragment') return null;
    return {
      CUSTOM_FRAGMENT_DEFINITIONS: `#ifdef DEEPWATER_MIST
${mistGlsl}
#endif`,
      // PBR provides vPositionW (fragment world position) and vEyePosition (camera).
      CUSTOM_FRAGMENT_BEFORE_FRAGCOLOR: `#ifdef DEEPWATER_MIST
finalColor.rgb = applyMist(
  finalColor.rgb,
  vPositionW,
  vEyePosition.xyz,
  dwMistColor,
  dwMistAir,
  dwMistSea,
  dwMistHeight,
  dwMistFar,
  dwMistNear,
  dwMistNearRange
);
#endif`,
    };
  }
}
